import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { IngredientDetector } from "../src/ingredient_detector"

export default class extends Controller {
    static targets = ["searchInput", "searchResults", "clearButton", "searchContainer", "leftArrow", "searchIcon"]
    static values = {
        toggle: { type: Boolean, default: false },
        inputValue: { type: String, default: "" }
    }

    initialize() {
        this.previousInputValue = "";
    }

    connect() {
        this.boundHideResultsIfOutside = this.hideResultsIfOutside.bind(this)
        document.addEventListener("click", this.boundHideResultsIfOutside)

        if (this.hasSearchInputTarget) {
            this.searchInputTarget.addEventListener('paste', this.handlePaste.bind(this));
            this.inputValueValue = this.searchInputTarget.value || "";
        }
    }

    disconnect() {
        document.removeEventListener("click", this.boundHideResultsIfOutside)
        if (this.hasSearchInputTarget) {
            this.searchInputTarget.removeEventListener('paste', this.handlePaste.bind(this));
        }
    }

    toggleSearch(event) {
        event.stopPropagation();
        this.toggleValue = !this.toggleValue;
        if (!this.toggleValue) {
            this.clearInput();
        }
    }

    showResults(event) {
        event.stopPropagation();
        this.toggleValue = true;
    }

    hideResults() {
        this.toggleValue = false;
    }

    hideResultsIfOutside(event) {
        if (!this.element.contains(event.target)) {
            this.hideResults();
        }
    }

    updateInput(event) {
        const currentValue = event.target.value;
        this.inputValueValue = currentValue;

        const textAdded = currentValue.length - this.previousInputValue.length;
        if (textAdded >= 20) {
            this.checkForIngredientListPaste(currentValue);
        }

        this.previousInputValue = currentValue;
    }

    clearInput(event) {
        if (event) {
            event.stopPropagation();
        }
        this.inputValueValue = "";
        this.searchInputTarget.value = "";
        const url = new URL('/products', window.location.origin);
        url.searchParams.set('product_filter[search]', '');
        Turbo.visit(url);
    }

    handleSubmit(event) {
        if (event.key === 'Enter' || event.type === 'submit') {
            event.preventDefault();
            const searchTerm = this.searchInputTarget.value;
            const url = new URL('/products', window.location.origin);
            url.searchParams.set('product_filter[search]', searchTerm);
            Turbo.visit(url);
        }
    }

    handlePaste(event) {
        const pastedText = (event.clipboardData || window.clipboardData).getData('text/plain').trim();

        if (IngredientDetector.isIngredientList(pastedText)) {
            event.preventDefault();
            IngredientDetector.submitForAnalysis(pastedText);
            this.searchInputTarget.blur();
            this.toggleValue = false;
        }
    }

    checkForIngredientListPaste(text) {
        if (IngredientDetector.isIngredientList(text)) {
            IngredientDetector.submitForAnalysis(text);
            this.searchInputTarget.blur();
            this.toggleValue = false;
        }
    }

    toggleValueChanged() {
        if (this.hasSearchContainerTarget) {
            if (this.toggleValue) {
                this.searchContainerTarget.classList.add("z-50");
            } else {
                this.searchContainerTarget.classList.remove("z-50");
            }
        }

        if (this.hasSearchResultsTarget) {
            if (this.toggleValue) {
                this.searchResultsTarget.classList.remove("hidden");
            } else {
                this.searchResultsTarget.classList.add("hidden");
            }
        }

        if (this.toggleValue) {
            if (this.hasSearchIconTarget) {
                this.searchIconTarget.classList.add("hidden");
            }
            if (this.hasLeftArrowTarget) {
                this.leftArrowTarget.classList.remove("hidden");
            }
        } else {
            if (this.hasSearchIconTarget) {
                this.searchIconTarget.classList.remove("hidden");
            }
            if (this.hasLeftArrowTarget) {
                this.leftArrowTarget.classList.add("hidden");
            }
        }

        this.updateRingClass();

        // Dispatch event to notify navigation about toggle state
        const event = new CustomEvent('search-bar:toggle', {
            detail: { active: this.toggleValue },
            bubbles: true
        });
        this.element.dispatchEvent(event);
    }

    inputValueValueChanged() {
        if (this.hasClearButtonTarget) {
            if (this.inputValueValue.length > 0 && this.toggleValue) {
                this.clearButtonTarget.classList.remove("hidden");
            } else {
                this.clearButtonTarget.classList.add("hidden");
            }
        }
    }

    updateRingClass() {
        if (this.hasSearchContainerTarget) {
            if (this.toggleValue) {
                this.searchContainerTarget.classList.remove("ring-transparent", "!ring-1");
                this.searchContainerTarget.classList.add("ring-warm-gray-500", "ring-2", "z-[200]");
            } else {
                this.searchContainerTarget.classList.remove("ring-warm-gray-500", "ring-2", "z-[200]");
                this.searchContainerTarget.classList.add("lg:hover:ring-warm-gray-400", "ring-transparent", "!ring-1");
            }
        }
    }
}
